// Static demo-mode fill-in data for TeamDashboard.component.jsx (Department
// Dashboard). Shapes must match exactly what each DashboardOverviewNew card
// component expects — see src/views/DashboardOverviewNew/components/*.

import { appRoutes } from '@routes';
import { TEAM_PERFORMANCE_DATA } from '../DashboardOverviewNew/data/dashboardOverviewNew.data';

// Consumed by OverallProductivity: score/trendPct/teamAvg are plain numbers
// (rendered as `${score}%` etc, no extra formatting); label is one of
// 'Good' | 'Average' | 'Low' per `scoreLabel`; trendUp is a boolean.
export const DEMO_OVERALL_PRODUCTIVITY_DATA = {
  score: 88,
  label: 'Good',
  trendPct: 6,
  trendUp: true,
  teamAvg: 79,
};

// Consumed by KeyHighlights: array of 5 highlight cards, each rendered via
// ICON_MAP[iconType] (clock/coffee/users/target/shield). `trendPct !== 0`
// gates whether the trend badge renders; `sublabelLink` gates whether the
// sublabel renders as a <Link to={sublabelTo}> or a plain span with
// sublabel + optional sublabelSuffix.
export const DEMO_KEY_HIGHLIGHTS_DATA = [
  {
    id: 'active-time',
    label: 'Active Time',
    value: '32h 30m',
    trendPct: 9,
    trendUnit: '%',
    trendUp: true,
    sublabel: '88%',
    sublabelSuffix: 'of tracked time',
    iconType: 'clock',
    iconBg: '#EEF4FF',
    iconColor: '#4F7EF8',
  },
  {
    id: 'idle-time',
    label: 'Idle Time',
    value: '4h 35m',
    trendPct: 11,
    trendUnit: '%',
    trendUp: false,
    sublabel: '12%',
    sublabelSuffix: 'of tracked time',
    iconType: 'coffee',
    iconBg: '#FFF6ED',
    iconColor: '#f59e0b',
  },
  {
    id: 'attendance',
    label: 'Attendance',
    value: '100%',
    trendPct: 2,
    trendUnit: '%',
    trendUp: true,
    sublabel: '5/5',
    sublabelSuffix: 'present',
    iconType: 'users',
    iconBg: '#ECFDF3',
    iconColor: '#22c55e',
  },
  {
    id: 'focus-time',
    label: 'Focus Time',
    value: '20h 07m',
    trendPct: 14,
    trendUnit: '%',
    trendUp: true,
    sublabel: '62%',
    sublabelSuffix: 'of tracked time',
    iconType: 'target',
    iconBg: '#EEF4FF',
    iconColor: '#4F7EF8',
  },
  {
    id: 'alerts',
    label: 'Alerts',
    value: '3',
    trendPct: 4,
    trendUnit: '',
    trendUp: false,
    sublabel: 'View all alerts',
    sublabelLink: true,
    sublabelTo: appRoutes().alerts,
    iconType: 'shield',
    iconBg: '#FFF1F3',
    iconColor: '#ef4444',
  },
];

// Consumed by ProductivityOverTime: recharts LineChart data, `dataKey="time"`
// on the x-axis must line up with DEMO_CHART_TICKS' string values.
export const DEMO_CHART_TICKS = [
  '12 AM',
  '4 AM',
  '8 AM',
  '12 PM',
  '4 PM',
  '8 PM',
];

const HOUR_LABELS = [
  '12 AM',
  '1 AM',
  '2 AM',
  '3 AM',
  '4 AM',
  '5 AM',
  '6 AM',
  '7 AM',
  '8 AM',
  '9 AM',
  '10 AM',
  '11 AM',
  '12 PM',
  '1 PM',
  '2 PM',
  '3 PM',
  '4 PM',
  '5 PM',
  '6 PM',
  '7 PM',
  '8 PM',
  '9 PM',
  '10 PM',
  '11 PM',
];

// A day-shaped curve: quiet overnight, ramping through the morning, a small
// lunchtime dip, an afternoon peak, tapering into the evening.
const DAY_CURVE = [
  5, 3, 2, 2, 4, 8, 15, 28, 45, 68, 82, 88, 75, 65, 80, 90, 92, 85, 60, 35, 20,
  12, 8, 5,
];

export const DEMO_PRODUCTIVITY_OVER_TIME_DATA = HOUR_LABELS.map((time, i) => ({
  time,
  score: DAY_CURVE[i],
  teamAvg: Math.max(0, Math.round(DAY_CURVE[i] * 0.93)),
}));

// Consumed by ActivityBreakdown (via DonutCard): segments' `value` is a
// percent (0-100), rendered as `${value}%`; should sum to ~100.
export const DEMO_ACTIVITY_BREAKDOWN_DATA = {
  totalTime: '32h 30m',
  label: 'Total Tracked',
  segments: [
    { name: 'Productive', value: 65, color: '#4F7EF8' },
    { name: 'Meetings', value: 15, color: '#a78bfa' },
    { name: 'Breaks', value: 12, color: '#f59e0b' },
    { name: 'Idle', value: 8, color: '#E5E7EB' },
  ],
};

// Consumed by ActivityHeatmap: 7 days × 24 hourly slots, intensity 0.0-1.0.
export const DEMO_ACTIVITY_HEATMAP_DATA = [
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
  'Sun',
].map((day, dayIndex) => {
  const isWeekend = dayIndex >= 5;
  const scale = isWeekend ? 0.3 : 1;
  return {
    day,
    slots: DAY_CURVE.map((v) => Math.round((v / 100) * scale * 100) / 100),
  };
});

// Consumed by TopApplications: up to 5 tiles, `pct` values needn't sum to
// 100 (each is relative to total tracked time independently). Times are the
// SUM of this app's usage across all 5 team members, not one person's —
// e.g. Visual Studio Code's 12h 00m is the combined total across the team.
export const DEMO_TOP_APPLICATIONS_DATA = [
  { name: 'Visual Studio Code', time: '12h 00m', pct: 37, color: '#4F7EF8' },
  { name: 'Google Chrome', time: '8h 00m', pct: 25, color: '#22c55e' },
  { name: 'Slack', time: '4h 20m', pct: 13, color: '#7a5af8' },
  { name: 'Figma', time: '3h 00m', pct: 9, color: '#f59e0b' },
  { name: 'Terminal', time: '2h 30m', pct: 8, color: '#ef4444' },
];

// Consumed by FocusVsDistraction (via DonutCard): segments' `pct` should
// sum to ~100; `time` is the pre-formatted duration shown alongside it.
export const DEMO_FOCUS_VS_DISTRACTION_DATA = {
  focusPct: 62,
  label: 'Focused',
  segments: [
    { name: 'Focus', pct: 62, time: '20h 07m', color: '#4F7EF8' },
    { name: 'Distraction', pct: 38, time: '12h 23m', color: '#f59e0b' },
  ],
};

// Consumed by AIInsights: iconType must be one of trending-up/trending-down/
// alert/star/warning/app (see AIInsights.component.jsx's ICON_MAP).
export const DEMO_AI_INSIGHTS_DATA = [
  {
    id: 'insight-1',
    iconType: 'trending-up',
    iconBg: '#ECFDF3',
    iconColor: '#22c55e',
    title: 'Productivity trending up',
    desc: 'Team productivity is up 6% compared to last week.',
  },
  {
    id: 'insight-2',
    iconType: 'star',
    iconBg: '#FFF6ED',
    iconColor: '#f59e0b',
    title: 'Top performer this week',
    desc: 'Natalie Brooks led the team with a 92% productivity score.',
  },
  {
    id: 'insight-3',
    iconType: 'warning',
    iconBg: '#FFF1F3',
    iconColor: '#ef4444',
    title: '2 members below average',
    desc: 'Consider checking in — activity has dipped over the last 3 days.',
  },
  {
    id: 'insight-4',
    iconType: 'app',
    iconBg: '#EEF4FF',
    iconColor: '#4F7EF8',
    title: 'New tool adopted',
    desc: 'Figma usage grew 20% this week across the team.',
  },
];

// Consumed by RecentAlerts: same shape produced by the real
// useTeamDashboardData hook's `recentAlertsData` mapping.
export const DEMO_RECENT_ALERTS_DATA = [
  {
    id: 'alert-1',
    iconType: 'triangle',
    iconBg: '#FFF1F3',
    iconColor: '#ef4444',
    title: 'Low activity detected',
    subtitle: 'fatima.alsayed@meridianlabs.com',
    time: '2h ago',
  },
  {
    id: 'alert-2',
    iconType: 'bar-chart',
    iconBg: '#FFF6ED',
    iconColor: '#f59e0b',
    title: 'Idle time threshold exceeded',
    subtitle: 'fatima.alsayed@meridianlabs.com',
    time: '5h ago',
  },
  {
    id: 'alert-3',
    iconType: 'triangle',
    iconBg: '#FFF1F3',
    iconColor: '#ef4444',
    title: 'Late check-in',
    subtitle: 'michael.chen@meridianlabs.com',
    time: '1d ago',
  },
];

// Consumed by TeamPerformance — reuses the already-built sample roster from
// DashboardOverviewNew (previously unused placeholder data).
export const DEMO_TEAM_PERFORMANCE_DATA = TEAM_PERFORMANCE_DATA;

// Consumed by AttendanceSummary (via DonutCard): `value` is a percent.
export const DEMO_ATTENDANCE_SUMMARY_DATA = {
  overall: 100,
  segments: [
    { name: 'Present', value: 100, color: '#22c55e' },
    { name: 'Absent', value: 0, color: '#E5E7EB' },
  ],
};
